import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './views/home/home.component';
import { SignupComponent } from './views/auth/signup/signup.component';
import { MapComponent } from './views/map/map.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CardMediumComponent } from './components/card-medium/card-medium.component';
import { CardProjectComponent } from './components/card-project/card-project.component';
import { CardSmallComponent } from './components/card-small/card-small.component';
import { WidgetOneComponent } from './components/card-small/widget-list/widget-one/widget-one.component';
import { WidgetTwoComponent } from './components/card-small/widget-list/widget-two/widget-two.component';
import { WidgetThreeComponent } from './components/card-small/widget-list/widget-three/widget-three.component';
import { WidgetListComponent } from './components/card-small/widget-list/widget-list.component';
import { ToolkitListComponent } from './components/card-small/toolkit-list/toolkit-list.component';
import { ActionListComponent } from './components/card-small/action-list/action-list.component';
import { ToggleSwitchComponent } from './components/toggle-switch/toggle-switch.component';
import { LayerListComponent } from './components/card-small/layer-list/layer-list.component';
import { MenuListComponent } from './components/card-small/menu-list/menu-list.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import * as echarts from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import { UsersComponent } from './views/home/users/users.component';
import { CardUserComponent } from './components/card-user/card-user.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ProjectsComponent } from './views/home/projects/projects.component';
import { CreateComponent as ProjectCreateComponent } from './views/home/projects/create/create.component';
import { EditComponent as ProjectEditComponent } from './views/home/projects/edit/edit.component';
import { ListComponent as UserListComponent } from './views/home/users/list/list.component';
import { CreateComponent as UserCreateComponent } from './views/home/users/create/create.component';
import { EditComponent as UserEditComponent } from './views/home/users/edit/edit.component';
import { DetailsComponent as ProjectDetailsComponent } from './views/home/projects/details/details.component';
import { ProjectListComponent } from './components/card-small/project-list/project-list.component';
import { AnnotationsComponent as ProjectDetailsAnnotationsComponent } from './views/home/projects/details/annotations/annotations.component';
import { DiscussionsComponent as ProjectDetailsDiscussionsComponent } from './views/home/projects/details/discussions/discussions.component';
import { TasksComponent as ProjectDetailsTasksComponent } from './views/home/projects/details/tasks/tasks.component';
import { GalleryComponent as ProjectDetailsGalleryComponent } from './views/home/projects/details/gallery/gallery.component';
import { HistoryComponent as ProjectDetailsHistoryComponent } from './views/home/projects/details/history/history.component';
import { DetailsComponent as ProjectDetailsDetailsComponent } from './views/home/projects/details/info/info.component';
import { ToastrModule } from 'ngx-toastr';
import { CardAnnotationComponent } from './components/card-annotation/card-annotation.component';
import { CreateAnnotationComponent } from './views/home/projects/details/annotations/create-annotation/create-annotation.component';
import { ListAnnotationComponent } from './views/home/projects/details/annotations/list-annotation/list-annotation.component';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { CardDiscussionComponent } from './components/card-discussion/card-discussion.component';
import { CardTaskComponent } from './components/card-task/card-task.component';
import { AgGridModule } from 'ag-grid-angular';
import { TableActionButtonComponent } from './components/table-action-button/table-action-button.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { EditAnnotationComponent } from './views/home/projects/details/annotations/edit-annotation/edit-annotation.component';
import { AuthInterceptor } from './helpers/auth.interceptor';
import { AuthGuard } from './helpers/auth.guard';
import { ComponentsComponent } from './views/home/projects/details/components/components.component';
import { CreateComponent } from './views/home/projects/details/components/create/create.component';
import { EditComponent } from './views/home/projects/details/components/edit/edit.component';
import { NavigationService } from './services/navigation/navigation.service';
import { ResetPasswordComponent } from './views/auth/reset-password/reset-password.component';
import { CreateDiscussionComponent } from './views/home/projects/details/discussions/create-discussion/create-discussion.component';
import { EditDiscussionComponent } from './views/home/projects/details/discussions/edit-discussion/edit-discussion.component';
import { ListDiscussionComponent } from './views/home/projects/details/discussions/list-discussion/list-discussion.component';
import { CreateTaskComponent } from './views/home/projects/details/tasks/create-task/create-task.component';
import { EditTaskComponent } from './views/home/projects/details/tasks/edit-task/edit-task.component';
import { ListTaskComponent } from './views/home/projects/details/tasks/list-task/list-task.component';
import { TaskFormModule } from './components/task-form/task-form.module';
import { ViewUserComponent } from './views/home/users/view-user/view-user.component';
import { PotreeCesiumViewerComponent } from './components/potree-cesium-viewer/potree-cesium-viewer.component';
import { DronesComponent } from './views/home/drones/drones.component';
import { CardDroneComponent } from './views/home/drones/card-drone/card-drone.component';
import { ListDroneComponent } from './views/home/drones/list-drone/list-drone.component';
import { CreateDroneComponent } from './views/home/drones/create-drone/create-drone.component';
import { SplitViewComponent } from './components/split-view/split-view.component';
import { AngularSplitModule } from 'angular-split';
import { DetailTaskComponent } from './views/home/projects/details/tasks/detail-task/detail-task.component';
import { FileUploadModule } from './components/file-upload/file-upload.module';
import { ChartsModule } from './components/charts/charts.module';
import { UploadMetaDataComponent } from './views/home/projects/upload-meta-data/upload-meta-data.component';
import { CardXsmallComponent } from './components/card-xsmall/card-xsmall.component';
import { ModalModule } from 'ngx-bootstrap/modal';
import { SummaryModalComponent } from './views/home/projects/details/summary-modal/summary-modal.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { NgxImageCompressService } from 'ngx-image-compress';

import { NotificationsComponent } from './views/home/notifications/notifications.component';
import { NotificationSettingsComponent } from './views/home/notifications/notification-settings/notification-settings.component';
import { Gallery2Component } from './views/home/projects/details/gallery2/gallery2.component';
import { SearchListComponent } from './components/card-small/search-list/search-list.component';
import { SimpleSearchModule } from './components/simple-search/simple-search.module';
import { CardLargeModule } from './components/card-large/card-large.module';
import { AnnotationsTableModule } from './components/annotations-table/annotations-table.module';

import { VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';

import { CsvModule } from '@ctrl/ngx-csv';
import { DirectivesModule } from './directives/directives.module';
import { OpenlayerComponent } from './components/openlayer/openlayer.component';

import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';
import { environment } from 'src/environments/environment';
import { FaqComponent } from './views/home/projects/details/faq/faq.component';

const config: SocketIoConfig = { url: environment.websocketUrl, options: {} };

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    SignupComponent,
    MapComponent,
    CardMediumComponent,
    CardProjectComponent,
    CardSmallComponent,
    WidgetOneComponent,
    WidgetTwoComponent,
    WidgetThreeComponent,
    WidgetListComponent,
    ToolkitListComponent,
    ActionListComponent,
    ToggleSwitchComponent,
    LayerListComponent,
    MenuListComponent,
    BreadcrumbComponent,
    UsersComponent,
    UserCreateComponent,
    UserEditComponent,
    CardUserComponent,
    ProjectsComponent,
    ProjectCreateComponent,
    ProjectEditComponent,
    ProjectDetailsComponent,
    ProjectListComponent,
    ProjectDetailsAnnotationsComponent,
    ProjectDetailsDiscussionsComponent,
    ProjectDetailsTasksComponent,
    ProjectDetailsGalleryComponent,
    ProjectDetailsHistoryComponent,
    ProjectDetailsDetailsComponent,
    CardAnnotationComponent,
    CreateAnnotationComponent,
    ListAnnotationComponent,
    CardDiscussionComponent,
    CardTaskComponent,
    TableActionButtonComponent,
    UserListComponent,
    EditAnnotationComponent,
    ComponentsComponent,
    CreateComponent,
    EditComponent,
    ResetPasswordComponent,
    CreateDiscussionComponent,
    EditDiscussionComponent,
    ListDiscussionComponent,
    CreateTaskComponent,
    EditTaskComponent,
    ListTaskComponent,
    ViewUserComponent,
    PotreeCesiumViewerComponent,
    DronesComponent,
    CardDroneComponent,
    ListDroneComponent,
    CreateDroneComponent,
    SplitViewComponent,
    DetailTaskComponent,
    UploadMetaDataComponent,
    CardXsmallComponent,
    SummaryModalComponent,
    NotificationsComponent,
    NotificationSettingsComponent,
    Gallery2Component,
    SearchListComponent,
    OpenlayerComponent,
    FaqComponent,
  ],
  imports: [
    MatProgressBarModule,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    ScrollingModule,
    NgxSpinnerModule,
    BrowserAnimationsModule,
    NgxEchartsModule.forRoot({
      echarts,
    }),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    ToastrModule.forRoot({
      timeOut: 2000,
      preventDuplicates: true,
    }),
    NgxDatatableModule,
    /** START: In-house components module */
    TaskFormModule,
    FileUploadModule,
    SimpleSearchModule,
    CardLargeModule,
    AnnotationsTableModule,
    DirectivesModule,
    /** END: In-house components module */
    BrowserModule,
    AgGridModule.withComponents([TableActionButtonComponent]),
    MatSidenavModule,
    ChartsModule,
    AngularSplitModule.forRoot(),
    CarouselModule.forRoot(),
    ModalModule.forRoot(),
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    CsvModule,
    SocketIoModule.forRoot(config),
  ],
  providers: [
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    NavigationService,
    NgxImageCompressService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/lang/', '.json');
}
